/* ==========================================================================
   HAPŪ HELPERS — OVULATION CALCULATOR
   --------------------------------------------------------------------------
   All maths runs on the visitor's device. Nothing is transmitted or stored.

   OVU_CONFIG lives here rather than in the section schema on purpose. These
   are clinical parameters, not copy.

   REVISION — ovulation is now derived from the cycle day rather than by
   counting back from the next period. Cycle day 1 is the first day of the
   last period, so cycle day N falls on lastPeriod + (N - 1). Ovulation is
   estimated on cycle day (cycleLength - 14), putting a 28 day cycle on day 14
   and a 35 day cycle on day 21. The next period date is unchanged.
   ========================================================================== */

(function () {
  "use strict";

  var root = document.querySelector("[data-hh-ovu]");
  if (!root) return;
  if (root.getAttribute("data-hh-ovu-init") === "true") return;
  root.setAttribute("data-hh-ovu-init", "true");

  /* -- Configuration -- */
  var OVU_CONFIG = {
    ovulationCycleDayOffset: 14,  // ovulation cycle day = cycle length minus this (APPROVED)
    fertileWindowLeadDays: 5,     // window opens this many days before estimated ovulation (APPROVED)
    remainingFertilityDays: 1,    // days after ovulation shown as possible remaining fertility (APPROVED)
    higherLikelihoodLeadDays: 2,  // days before ovulation flagged as often more likely (APPROVED)
    defaultCycleLength: 28,
    selectMin: 20,                // dropdown lower bound (APPROVED)
    selectMax: 40,                // dropdown upper bound (APPROVED)
    cautionShort: 20,             // 20 or below triggers the clinical caution (APPROVED)
    cautionLong: 36,              // 36 or above triggers the clinical caution (APPROVED)
    earlyTestingCycleDay: 7,      // testing starting on this cycle day or earlier triggers the overlap note (APPROVED)
    maxLastPeriodAgeDays: 60,     // last period older than this is too old for a current-cycle estimate (REVIEW)
    variationThresholdDays: 4,    // range across recent cycles that triggers the variation message (REVIEW)
    ovaDetectStartLeadDays: 5     // start testing this many days before estimated ovulation (APPROVED)
  };

  var MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];
  var DOW_SHORT = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];
  var DOW_FULL = ["Sunday","Monday","Tuesday","Wednesday","Thursday","Friday","Saturday"];

  /* ------------------------------------------------------------------
     Date helpers. Everything sits at UTC noon so daylight saving and
     timezone offsets can never shift a date by a day.
     ------------------------------------------------------------------ */
  function parseDate(str) {
    if (!str) return null;
    var parts = String(str).split("-");
    if (parts.length !== 3) return null;
    var d = new Date(Date.UTC(+parts[0], +parts[1] - 1, +parts[2], 12, 0, 0));
    return isNaN(d.getTime()) ? null : d;
  }
  function toISO(date) {
    var m = String(date.getUTCMonth() + 1);
    var d = String(date.getUTCDate());
    if (m.length < 2) m = "0" + m;
    if (d.length < 2) d = "0" + d;
    return date.getUTCFullYear() + "-" + m + "-" + d;
  }
  function addDays(date, n) {
    var d = new Date(date.getTime());
    d.setUTCDate(d.getUTCDate() + n);
    return d;
  }
  function daysBetween(a, b) {
    return Math.round((b.getTime() - a.getTime()) / 86400000);
  }
  function sameYMD(a, b) {
    return a.getUTCFullYear() === b.getUTCFullYear()
      && a.getUTCMonth() === b.getUTCMonth()
      && a.getUTCDate() === b.getUTCDate();
  }
  function fmt(date) {
    return date.getUTCDate() + " " + MONTHS[date.getUTCMonth()] + " " + date.getUTCFullYear();
  }
  function fmtPadded(date) {
    var d = String(date.getUTCDate());
    if (d.length < 2) d = "0" + d;
    return d + " " + MONTHS[date.getUTCMonth()] + " " + date.getUTCFullYear();
  }
  function today() {
    var n = new Date();
    return new Date(Date.UTC(n.getFullYear(), n.getMonth(), n.getDate(), 12, 0, 0));
  }
  function prefersReduced() {
    return window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  }

  /* ==================================================================
     CUSTOM DATE PICKER

     Replaces the native control so the calendar carries brand styling,
     the whole box opens the picker rather than just the icon, and the
     trigger always shows a readable value. An empty native date field
     renders as a blank box on iOS, which is the mobile bug this fixes.
     ================================================================== */
  var pickers = [];

  function closeAllExcept(except) {
    for (var i = 0; i < pickers.length; i++) {
      if (pickers[i].wrap !== except) pickers[i].close();
    }
  }

  function createDatePicker(wrap) {
    var input = wrap.querySelector("[data-dp-input]");
    var trigger = wrap.querySelector("[data-dp-trigger]");
    var display = wrap.querySelector("[data-dp-display]");
    var panel = wrap.querySelector("[data-dp-panel]");
    if (!input || !trigger || !display || !panel) return null;

    var placeholder = display.textContent;
    var maxDate = wrap.getAttribute("data-dp-max") === "today" ? today() : null;
    var viewDate = today();
    var focusDate = today();
    var isOpen = false;

    /* Set while the grid is being rebuilt. Removing the focused or clicked
       node mid-render used to fire focusout and the document click handler
       with a detached target, which closed the panel. That was the month
       arrows shutting the picker instead of paging. */
    var renderLock = false;
    function lock(fn) {
      renderLock = true;
      fn();
      setTimeout(function () { renderLock = false; }, 0);
    }

    /* The head and foot are built once per open and never destroyed, so the
       arrow buttons stay attached across a month change. */
    var ui = null;

    function selected() { return parseDate(input.value); }

    function setValue(date) {
      input.value = date ? toISO(date) : "";
      display.textContent = date ? fmtPadded(date) : placeholder;
      wrap.classList.toggle("has-value", !!date);
      input.dispatchEvent(new Event("change", { bubbles: true }));
    }

    function isDisabled(date) {
      return maxDate ? date > maxDate : false;
    }

    function buildChrome() {
      panel.innerHTML = "";

      var head = document.createElement("div");
      head.className = "hh-ovu-dp__head";

      var prev = document.createElement("button");
      prev.type = "button";
      prev.className = "hh-ovu-dp__nav";
      prev.setAttribute("aria-label", "Previous month");
      prev.innerHTML = "&#9664;";
      prev.addEventListener("click", function (e) {
        e.preventDefault();
        e.stopPropagation();
        shiftMonth(-1);
      });

      var title = document.createElement("div");
      title.className = "hh-ovu-dp__title";
      title.setAttribute("aria-live", "polite");
      var mo = document.createElement("span");
      mo.className = "hh-ovu-dp__month";
      var yr = document.createElement("span");
      yr.className = "hh-ovu-dp__year";
      title.appendChild(mo);
      title.appendChild(yr);

      var next = document.createElement("button");
      next.type = "button";
      next.className = "hh-ovu-dp__nav";
      next.setAttribute("aria-label", "Next month");
      next.innerHTML = "&#9654;";
      next.addEventListener("click", function (e) {
        e.preventDefault();
        e.stopPropagation();
        shiftMonth(1);
      });

      head.appendChild(prev);
      head.appendChild(title);
      head.appendChild(next);
      panel.appendChild(head);

      var gridWrap = document.createElement("div");
      gridWrap.className = "hh-ovu-dp__grid";
      gridWrap.setAttribute("role", "grid");
      gridWrap.addEventListener("keydown", onGridKey);
      panel.appendChild(gridWrap);

      var foot = document.createElement("div");
      foot.className = "hh-ovu-dp__foot";
      var todayBtn = document.createElement("button");
      todayBtn.type = "button";
      todayBtn.className = "hh-ovu-dp__foot-btn";
      todayBtn.textContent = "Today";
      todayBtn.addEventListener("click", function (e) {
        e.preventDefault();
        e.stopPropagation();
        setValue(today());
        focusDate = today();
        close(true);
      });
      var clearBtn = document.createElement("button");
      clearBtn.type = "button";
      clearBtn.className = "hh-ovu-dp__foot-btn hh-ovu-dp__foot-btn--muted";
      clearBtn.textContent = "Clear";
      clearBtn.addEventListener("click", function (e) {
        e.preventDefault();
        e.stopPropagation();
        setValue(null);
        close(true);
      });
      foot.appendChild(todayBtn);
      foot.appendChild(clearBtn);
      panel.appendChild(foot);

      ui = { month: mo, year: yr, next: next, grid: gridWrap };
    }

    function refreshHead() {
      var y = viewDate.getUTCFullYear();
      var m = viewDate.getUTCMonth();
      ui.month.textContent = MONTHS[m];
      ui.year.textContent = " " + y;
      ui.grid.setAttribute("aria-label", MONTHS[m] + " " + y);
      ui.next.disabled = !!(maxDate && new Date(Date.UTC(y, m + 1, 1, 12)) > maxDate);
    }

    function renderGrid() {
      var y = viewDate.getUTCFullYear();
      var m = viewDate.getUTCMonth();
      var sel = selected();
      var now = today();

      ui.grid.innerHTML = "";

      var headRow = document.createElement("div");
      headRow.className = "hh-ovu-dp__row";
      headRow.setAttribute("role", "row");
      for (var hi = 0; hi < DOW_SHORT.length; hi++) {
        var c = document.createElement("span");
        c.className = "hh-ovu-dp__dow";
        c.setAttribute("role", "columnheader");
        c.setAttribute("aria-label", DOW_FULL[hi]);
        c.textContent = DOW_SHORT[hi];
        headRow.appendChild(c);
      }
      ui.grid.appendChild(headRow);

      var firstOfMonth = new Date(Date.UTC(y, m, 1, 12));
      var gridStart = addDays(firstOfMonth, -firstOfMonth.getUTCDay());

      for (var w = 0; w < 6; w++) {
        var row = document.createElement("div");
        row.className = "hh-ovu-dp__row";
        row.setAttribute("role", "row");
        for (var dd = 0; dd < 7; dd++) {
          var cellDate = addDays(gridStart, w * 7 + dd);
          var btn = document.createElement("button");
          btn.type = "button";
          btn.className = "hh-ovu-dp__day";
          btn.setAttribute("role", "gridcell");
          btn.textContent = cellDate.getUTCDate();
          btn.setAttribute("aria-label", fmt(cellDate));
          btn.setAttribute("data-date", toISO(cellDate));

          if (cellDate.getUTCMonth() !== m) btn.className += " is-outside";
          if (sameYMD(cellDate, now)) btn.className += " is-today";
          if (sel && sameYMD(cellDate, sel)) {
            btn.className += " is-selected";
            btn.setAttribute("aria-selected", "true");
          } else {
            btn.setAttribute("aria-selected", "false");
          }
          if (isDisabled(cellDate)) {
            btn.disabled = true;
            btn.setAttribute("aria-disabled", "true");
          }
          btn.tabIndex = sameYMD(cellDate, focusDate) ? 0 : -1;

          btn.addEventListener("click", function (e) {
            e.preventDefault();
            e.stopPropagation();
            var picked = parseDate(this.getAttribute("data-date"));
            setValue(picked);
            focusDate = picked;
            close(true);
          });
          row.appendChild(btn);
        }
        ui.grid.appendChild(row);
      }
    }

    function paint() {
      lock(function () {
        refreshHead();
        renderGrid();
      });
    }

    function focusCell() {
      var target = ui.grid.querySelector('[data-date="' + toISO(focusDate) + '"]');
      if (target) target.focus({ preventScroll: true });
    }

    function shiftMonth(n) {
      /* The Next button disables itself past maxDate, but PageDown reached
         this directly. Paging into a fully disabled month left focusDate on a
         button that cannot take focus, so focus fell to the body and the arrow
         keys stopped responding. */
      var target = new Date(Date.UTC(viewDate.getUTCFullYear(), viewDate.getUTCMonth() + n, 1, 12));
      if (maxDate && target > maxDate) return;
      viewDate = target;
      paint();
      if (!ui.grid.querySelector('[data-date="' + toISO(focusDate) + '"]')) {
        focusDate = new Date(Date.UTC(viewDate.getUTCFullYear(), viewDate.getUTCMonth(), 1, 12));
        paint();
      }
    }

    function moveFocus(days) {
      var candidate = addDays(focusDate, days);
      if (isDisabled(candidate)) return;
      focusDate = candidate;
      if (focusDate.getUTCMonth() !== viewDate.getUTCMonth()
        || focusDate.getUTCFullYear() !== viewDate.getUTCFullYear()) {
        viewDate = new Date(Date.UTC(focusDate.getUTCFullYear(), focusDate.getUTCMonth(), 1, 12));
      }
      paint();
      focusCell();
    }

    function onGridKey(e) {
      switch (e.key) {
        case "ArrowLeft":  e.preventDefault(); moveFocus(-1); break;
        case "ArrowRight": e.preventDefault(); moveFocus(1); break;
        case "ArrowUp":    e.preventDefault(); moveFocus(-7); break;
        case "ArrowDown":  e.preventDefault(); moveFocus(7); break;
        case "Home":       e.preventDefault(); moveFocus(-focusDate.getUTCDay()); break;
        case "End":        e.preventDefault(); moveFocus(6 - focusDate.getUTCDay()); break;
        case "PageUp":     e.preventDefault(); shiftMonth(-1); focusCell(); break;
        case "PageDown":   e.preventDefault(); shiftMonth(1); focusCell(); break;
        case "Escape":     e.preventDefault(); close(true); break;
        case "Tab":        e.preventDefault(); close(true); break;
      }
    }

    function isCoarsePointer() {
      return !!(window.matchMedia && window.matchMedia("(pointer: coarse)").matches);
    }

    function openPanel(fromKeyboard) {
      if (isOpen) return;
      closeAllExcept(wrap);
      var sel = selected();
      focusDate = sel || (maxDate && today() > maxDate ? maxDate : today());
      viewDate = new Date(Date.UTC(focusDate.getUTCFullYear(), focusDate.getUTCMonth(), 1, 12));
      buildChrome();
      paint();
      panel.hidden = false;
      wrap.classList.add("is-open");
      trigger.setAttribute("aria-expanded", "true");
      isOpen = true;
      /* On a touch screen, programmatically focusing a day cell makes the page
         jump and serves no purpose, since there is no keyboard to drive it. */
      if (fromKeyboard || !isCoarsePointer()) focusCell();
    }

    function close(returnFocus) {
      if (!isOpen) return;
      panel.hidden = true;
      wrap.classList.remove("is-open");
      trigger.setAttribute("aria-expanded", "false");
      isOpen = false;
      if (returnFocus) trigger.focus({ preventScroll: true });
    }

    /* The whole box opens the picker, not just the icon. */
    trigger.addEventListener("click", function (e) {
      e.preventDefault();
      e.stopPropagation();
      if (isOpen) close(true); else openPanel(false);
    });
    trigger.addEventListener("keydown", function (e) {
      if (e.key === "ArrowDown") { e.preventDefault(); openPanel(true); }
    });

    /* Outside-close runs on pointerdown, not focusout.

       focusout was the mobile bug. iOS Safari does not move focus to a button
       when you tap it, so tapping a month arrow blurred whichever day cell had
       focus and reported relatedTarget as null. A null relatedTarget is not
       inside the wrapper, so the panel closed before the arrow's own click
       handler ever ran. On desktop the arrow does take focus, relatedTarget is
       the arrow, and the same code was fine. Hence desktop working and mobile
       not.

       pointerdown fires before focus moves and before any re-render, so the
       target is always the real element and always still attached. */
    function onOutsideDown(e) {
      if (!isOpen) return;
      if (!wrap.contains(e.target)) close(false);
    }
    if (window.PointerEvent) {
      document.addEventListener("pointerdown", onOutsideDown, true);
    } else {
      document.addEventListener("touchstart", onOutsideDown, true);
      document.addEventListener("mousedown", onOutsideDown, true);
    }

    /* Kept as a backstop for anything that reaches click without a preceding
       pointerdown. Guarded so a node removed by a re-render is never mistaken
       for an outside click. */
    document.addEventListener("click", function (e) {
      if (!isOpen || renderLock) return;
      if (!document.contains(e.target)) return;
      if (!wrap.contains(e.target)) close(false);
    });

    return {
      wrap: wrap,
      close: function () { close(false); },
      reset: function () { setValue(null); }
    };
  }

  var dpNodes = root.querySelectorAll("[data-dp]");
  for (var pi = 0; pi < dpNodes.length; pi++) {
    var pk = createDatePicker(dpNodes[pi]);
    if (pk) pickers.push(pk);
  }

  /* ==================================================================
     CALCULATOR
     ================================================================== */
  var el = function (id) { return document.getElementById(id); };
  var lastPeriod = el("ovuLastPeriod");
  var lastPeriodBtn = el("ovuLastPeriodBtn");
  var cycleSelect = el("ovuCycleLength");
  var helperToggle = el("ovuHelperToggle");
  var helperPanel = el("ovuHelperPanel");
  var p1 = el("ovuP1"), p2 = el("ovuP2"), p3 = el("ovuP3");
  var calcAvgBtn = el("ovuCalcAvg");
  var avgResult = el("ovuAvgResult");
  var avgValue = el("ovuAvgValue");
  var avgVary = el("ovuAvgVary");
  var threeErr = el("ovuThreeErr");
  var calcBtn = el("ovuCalculate");
  var results = el("ovuResults");
  var errSummary = el("ovuErrorSummary");
  var errList = el("ovuErrorList");
  var caution = el("ovuCaution");
  var earlyNote = el("ovuEarlyNote");
  var formGrid = el("ovuFormGrid");
  var aside = el("ovuAside");
  var regularity = null; // "similar" | "vary" | "unsure"

  if (!lastPeriod || !cycleSelect || !calcBtn) return;

  (function fillSelect() {
    for (var d = OVU_CONFIG.selectMin; d <= OVU_CONFIG.selectMax; d++) {
      var o = document.createElement("option");
      o.value = String(d);
      o.textContent = d + " days" + (d === OVU_CONFIG.defaultCycleLength ? " (most common average)" : "");
      cycleSelect.appendChild(o);
    }
    cycleSelect.value = String(OVU_CONFIG.defaultCycleLength);
  })();

  function setInlineError(target, errEl, msg) {
    if (!errEl) return;
    if (msg) {
      errEl.textContent = msg;
      errEl.classList.add("show");
      if (target) target.classList.add("error");
    } else {
      errEl.textContent = "";
      errEl.classList.remove("show");
      if (target) target.classList.remove("error");
    }
  }

  if (helperToggle && helperPanel) {
    helperToggle.addEventListener("click", function () {
      var opened = helperPanel.classList.toggle("open");
      helperToggle.setAttribute("aria-expanded", opened ? "true" : "false");
      helperToggle.textContent = opened
        ? helperToggle.getAttribute("data-close-label")
        : helperToggle.getAttribute("data-open-label");
    });
  }

  /* -- Three-date average -- */
  if (calcAvgBtn) {
    calcAvgBtn.addEventListener("click", function () {
      setInlineError(null, threeErr, "");
      avgResult.classList.remove("show");
      avgVary.classList.remove("show");

      var d1 = parseDate(p1.value), d2 = parseDate(p2.value), d3 = parseDate(p3.value);
      if (!d1 || !d2 || !d3) {
        setInlineError(null, threeErr, "Enter all three period start dates to work out your average.");
        return;
      }
      if (!(d1 < d2 && d2 < d3)) {
        setInlineError(null, threeErr, "Enter the dates oldest first, with three different dates.");
        return;
      }
      var c1 = daysBetween(d1, d2);
      var c2 = daysBetween(d2, d3);
      if (c1 <= 0 || c2 <= 0) {
        setInlineError(null, threeErr, "Those dates do not form two complete cycles. Please check them.");
        return;
      }

      var avg = Math.round((c1 + c2) / 2);
      var range = Math.abs(c1 - c2);
      var clamped = Math.min(Math.max(avg, OVU_CONFIG.selectMin), OVU_CONFIG.selectMax);

      avgValue.textContent = avg + " days";
      avgResult.classList.add("show");
      cycleSelect.value = String(clamped);
      setInlineError(cycleSelect, el("ovuCycleErr"), "");

      var clampNote = el("ovuAvgClamped");
      if (clampNote) {
        if (clamped !== avg) {
          clampNote.textContent = "Your average is outside the range this calculator covers, so we have used "
            + clamped + " days above. Treat the estimate with extra caution and consider speaking to a health professional about your cycle.";
          clampNote.classList.add("show");
        } else {
          clampNote.textContent = "";
          clampNote.classList.remove("show");
        }
      }

      if (range >= OVU_CONFIG.variationThresholdDays) {
        avgVary.classList.add("show");
        regularity = "vary";
        var varyRadio = root.querySelector('.hh-ovu-radio[data-reg="vary"]');
        if (varyRadio) {
          var all = root.querySelectorAll(".hh-ovu-radio");
          for (var ri = 0; ri < all.length; ri++) {
            all[ri].classList.remove("selected");
            all[ri].setAttribute("aria-checked", "false");
          }
          varyRadio.classList.add("selected");
          varyRadio.setAttribute("aria-checked", "true");
          /* Keep the roving tab stop on the checked option. Without this the
             tab stop stayed on the first radio while the third was checked. */
          for (var ti = 0; ti < all.length; ti++) all[ti].tabIndex = -1;
          varyRadio.tabIndex = 0;
        }
      }
    });
  }

  /* -- Regularity radios -- */
  var radios = Array.prototype.slice.call(root.querySelectorAll(".hh-ovu-radio"));
  radios.forEach(function (r, i) {
    function pick() {
      radios.forEach(function (x) {
        x.classList.remove("selected");
        x.setAttribute("aria-checked", "false");
        x.tabIndex = -1;
      });
      r.classList.add("selected");
      r.setAttribute("aria-checked", "true");
      r.tabIndex = 0;
      regularity = r.getAttribute("data-reg");
    }
    r.addEventListener("click", pick);
    r.addEventListener("keydown", function (e) {
      if (e.key === "Enter" || e.key === " ") { e.preventDefault(); pick(); return; }
      var next = null;
      if (e.key === "ArrowDown" || e.key === "ArrowRight") next = radios[(i + 1) % radios.length];
      if (e.key === "ArrowUp" || e.key === "ArrowLeft") next = radios[(i - 1 + radios.length) % radios.length];
      if (next) { e.preventDefault(); next.focus(); next.click(); }
    });
  });

  /* -- Live validation -- */
  lastPeriod.addEventListener("change", function () {
    var d = parseDate(lastPeriod.value);
    var errEl = el("ovuLastPeriodErr");
    if (!d) { setInlineError(lastPeriodBtn, errEl, ""); return; }
    if (d > today()) {
      setInlineError(lastPeriodBtn, errEl, "Your last period cannot start in the future.");
    } else if (daysBetween(d, today()) > OVU_CONFIG.maxLastPeriodAgeDays) {
      setInlineError(lastPeriodBtn, errEl, "That date is quite a while ago, so a current-cycle estimate may not be useful. Enter the first day of your most recent period.");
    } else {
      setInlineError(lastPeriodBtn, errEl, "");
    }
  });

  /* -- Main calculate -- */
  calcBtn.addEventListener("click", function () {
    var errors = [];
    errList.innerHTML = "";
    errSummary.classList.remove("show");
    caution.classList.remove("show");
    if (earlyNote) earlyNote.classList.remove("show");
    setInlineError(lastPeriodBtn, el("ovuLastPeriodErr"), "");
    setInlineError(cycleSelect, el("ovuCycleErr"), "");

    var lp = parseDate(lastPeriod.value);
    var cycle = parseInt(cycleSelect.value, 10);

    if (!lp) {
      var m1 = "Enter the first day of your last period.";
      setInlineError(lastPeriodBtn, el("ovuLastPeriodErr"), m1);
      errors.push({ msg: m1, focus: lastPeriodBtn });
    } else if (lp > today()) {
      var m2 = "Your last period cannot start in the future.";
      setInlineError(lastPeriodBtn, el("ovuLastPeriodErr"), m2);
      errors.push({ msg: m2, focus: lastPeriodBtn });
    } else if (daysBetween(lp, today()) > OVU_CONFIG.maxLastPeriodAgeDays) {
      var m3 = "That last period date is too old to give a useful current-cycle estimate. Enter your most recent period.";
      setInlineError(lastPeriodBtn, el("ovuLastPeriodErr"), m3);
      errors.push({ msg: m3, focus: lastPeriodBtn });
    }

    if (!cycle || isNaN(cycle)) {
      var m4 = "Select your average cycle length, or use the helper to work it out.";
      setInlineError(cycleSelect, el("ovuCycleErr"), m4);
      errors.push({ msg: m4, focus: cycleSelect });
    }

    if (errors.length) {
      errors.forEach(function (e) {
        var li = document.createElement("li");
        var a = document.createElement("a");
        a.textContent = e.msg;
        a.tabIndex = 0;
        a.addEventListener("click", function () { e.focus.focus(); });
        a.addEventListener("keydown", function (ev) {
          if (ev.key === "Enter" || ev.key === " ") { ev.preventDefault(); e.focus.focus(); }
        });
        li.appendChild(a);
        errList.appendChild(li);
      });
      errSummary.classList.add("show");
      errSummary.focus();
      return;
    }

    /* Clinical caution, unchanged. Short or long cycles may be irregular. */
    if (cycle <= OVU_CONFIG.cautionShort || cycle >= OVU_CONFIG.cautionLong) {
      caution.textContent = "If your average cycle is below 21 days or above 35 days, your cycle may be irregular, so speak to a health care professional for more information. You can still view the estimate below.";
      caution.classList.add("show");
    }

    compute(lp, cycle);
  });

  /* -- Compute + render -- */
  function compute(lp, cycle) {
    /* Cycle day 1 is the first day of the last period, so cycle day N sits on
       lastPeriod + (N - 1). Ovulation is estimated on cycle day
       (cycle - ovulationCycleDayOffset). The next period date is separate and
       stays at lastPeriod + cycle. */
    var nextPeriod = addDays(lp, cycle);
    var ovulationCycleDay = cycle - OVU_CONFIG.ovulationCycleDayOffset;
    var ovulation = addDays(lp, ovulationCycleDay - 1);

    var fertileStart = addDays(ovulation, -OVU_CONFIG.fertileWindowLeadDays);
    var fertileEnd = ovulation;
    var remaining = addDays(ovulation, OVU_CONFIG.remainingFertilityDays);
    var higherStart = addDays(ovulation, -OVU_CONFIG.higherLikelihoodLeadDays);
    var ovaDetectStart = addDays(ovulation, -OVU_CONFIG.ovaDetectStartLeadDays);

    /* Safety floor. Neither date can land before cycle day 1, even if the
       dropdown bounds are ever widened. */
    if (ovaDetectStart < lp) ovaDetectStart = lp;
    if (fertileStart < lp) fertileStart = lp;

    var testingCycleDay = daysBetween(lp, ovaDetectStart) + 1;

    el("ovuResWindow").innerHTML = fmt(fertileStart) + '<span class="to">to</span>' + fmt(fertileEnd);
    el("ovuResOvulation").textContent = fmt(ovulation);
    el("ovuResPeriod").textContent = fmt(nextPeriod);
    el("ovuBasis").textContent = "Based on a " + cycle + "-day average cycle beginning on " + fmt(lp)
      + ". Ovulation is estimated on cycle day " + ovulationCycleDay + ".";

    var testDate = el("ovuTestStartDate");
    if (testDate) testDate.textContent = fmt(ovaDetectStart);

    /* Early-testing overlap note. Separate from the clinical caution, and
       derived from the actual testing cycle day so it follows automatically
       if the testing lead ever changes again. */
    if (earlyNote) {
      if (testingCycleDay <= OVU_CONFIG.earlyTestingCycleDay) {
        earlyNote.textContent = "Because your cycle is on the shorter side, your fertile window opens on cycle day "
          + testingCycleDay + ", so you may still be bleeding when it is time to start testing. That is expected with shorter cycles and it is not a problem. "
          + "Starting on time matters more than waiting for bleeding to finish, because a shorter cycle usually means an earlier surge.";
        earlyNote.classList.add("show");
      } else {
        earlyNote.classList.remove("show");
      }
    }

    var stronger = el("ovuStronger");
    if (stronger) {
      if (regularity === "vary") stronger.classList.add("show");
      else stronger.classList.remove("show");
    }

    renderCalendar({
      fertileStart: fertileStart,
      fertileEnd: fertileEnd,
      ovulation: ovulation,
      remaining: remaining,
      higherStart: higherStart,
      nextPeriod: nextPeriod,
      ovaDetectStart: ovaDetectStart
    });

    results.classList.add("show");
    if (aside) aside.style.display = "none";
    if (formGrid) formGrid.classList.add("hh-ovu-card__grid--solo");

    results.scrollIntoView({ behavior: prefersReduced() ? "auto" : "smooth", block: "start" });
    var h = results.querySelector("h2");
    if (h) {
      h.setAttribute("tabindex", "-1");
      h.focus({ preventScroll: true });
    }
  }

  /* -- Calendar -- */
  function renderCalendar(d) {
    var wrap = el("ovuMonths");
    wrap.innerHTML = "";
    wrap.setAttribute("aria-hidden", "true");

    var summary = el("ovuCalSummary");
    if (summary) {
      var higherEnd = addDays(d.ovulation, -1);
      summary.textContent = "Calendar summary. Possible fertile window, "
        + fmt(d.fertileStart) + " to " + fmt(d.fertileEnd) + ". "
        + "Suggested day to start testing, " + fmt(d.ovaDetectStart) + ". "
        + "Conception is often more likely on " + fmt(d.higherStart) + " and " + fmt(higherEnd) + ". "
        + "Estimated ovulation, " + fmt(d.ovulation) + ". "
        + "Possible remaining fertility, " + fmt(d.remaining) + ". "
        + "Estimated next period, " + fmt(d.nextPeriod) + ".";
    }
    var startM = new Date(Date.UTC(d.fertileStart.getUTCFullYear(), d.fertileStart.getUTCMonth(), 1, 12));
    var endM = new Date(Date.UTC(d.nextPeriod.getUTCFullYear(), d.nextPeriod.getUTCMonth(), 1, 12));
    var cur = startM;
    while (cur <= endM) {
      wrap.appendChild(buildMonth(cur.getUTCFullYear(), cur.getUTCMonth(), d));
      cur = new Date(Date.UTC(cur.getUTCFullYear(), cur.getUTCMonth() + 1, 1, 12));
    }
  }

  /* Every cell gets a .dtags wrapper whether it has labels or not, so the
     day number sits in exactly the same place in tagged and untagged cells.
     Uneven child counts were what made some numbers ride higher than others. */
  function addTag(box, text, mod) {
    var t = document.createElement("span");
    t.className = "dtag" + (mod ? " dtag--" + mod : "");
    t.textContent = text;
    box.appendChild(t);
  }

  function buildMonth(year, month, d) {
    var monthEl = document.createElement("div");
    monthEl.className = "hh-ovu-month";

    var name = document.createElement("div");
    name.className = "hh-ovu-month__name";
    name.textContent = MONTHS[month] + " " + year;
    monthEl.appendChild(name);

    var grid = document.createElement("div");
    grid.className = "hh-ovu-month__grid";
    /* No ARIA grid role here. An ARIA grid needs row children and this is a
       flat 7-column layout, so the role was never valid. The grid is hidden
       from assistive tech and the summary written in renderCalendar carries
       the same information in a few lines instead of sixty day numbers. */

    for (var hi = 0; hi < DOW_SHORT.length; hi++) {
      var c = document.createElement("div");
      c.className = "hh-ovu-dow";
      c.textContent = DOW_SHORT[hi];
      grid.appendChild(c);
    }

    var firstDay = new Date(Date.UTC(year, month, 1, 12));
    var startWeekday = firstDay.getUTCDay();
    var daysInMonth = new Date(Date.UTC(year, month + 1, 0, 12)).getUTCDate();

    /* The first of the month is placed into its real weekday column and every
       following day flows on from there.

       This replaces the old approach of prepending filler divs carrying a
       "hh-ovu-day empty" class that was hidden with visibility:hidden. That
       relied on the filler boxes holding their grid cells, and on nothing else
       on the page matching the very generic class name "empty". Something was
       collapsing them, which pushed the 1st into the Sunday column on every
       month. Positioning the 1st directly removes the filler elements from the
       DOM altogether, so there is nothing left to collapse. */

    for (var day = 1; day <= daysInMonth; day++) {
      var date = new Date(Date.UTC(year, month, day, 12));
      var cell = document.createElement("div");
      cell.className = "hh-ovu-day";

      /* Offset the month into its correct starting column. CSS grid columns
         are 1-indexed, so Sunday is column 1. */
      if (day === 1 && startWeekday > 0) {
        cell.style.gridColumnStart = startWeekday + 1;
      }

      var num = document.createElement("span");
      num.className = "dnum";
      num.textContent = day;
      cell.appendChild(num);

      var tags = document.createElement("span");
      tags.className = "dtags";
      cell.appendChild(tags);

      var tagged = false, aria = fmt(date);
      var inFertile = date >= d.fertileStart && date <= d.fertileEnd;
      var isTestStart = d.ovaDetectStart && sameYMD(date, d.ovaDetectStart);

      if (sameYMD(date, d.ovulation)) {
        cell.classList.add("ovulation");
        addTag(tags, "Ovu");
        aria += ", estimated ovulation date";
        tagged = true;
      } else if (sameYMD(date, d.nextPeriod)) {
        cell.classList.add("period");
        addTag(tags, "Period");
        aria += ", estimated next period";
        tagged = true;
      } else if (sameYMD(date, d.remaining)) {
        cell.classList.add("remaining");
        addTag(tags, "+ Fert");
        aria += ", possible remaining fertility";
        tagged = true;
      } else if (inFertile) {
        cell.classList.add("fertile");
        aria += ", possible fertile window";
        if (isTestStart) {
          /* Testing start and the window opening are the same day now, so the
             cell carries both labels in their own colours. */
          cell.classList.add("testing");
          cell.classList.add("dual");
          addTag(tags, "Fertile", "fertile");
          addTag(tags, "Test", "test");
          aria += ", and the suggested day to start OvaDetect testing";
        } else {
          addTag(tags, "Fertile", "fertile");
        }
        if (date >= d.higherStart && date < d.ovulation) {
          cell.classList.add("higher");
          aria += ", often more likely";
        }
        tagged = true;
      } else if (isTestStart) {
        /* Only reachable if the two leads are ever given different values. */
        cell.classList.add("testing");
        addTag(tags, "Test", "test");
        aria += ", suggested day to start OvaDetect testing";
        tagged = true;
      }

      if (tagged) {
        cell.setAttribute("aria-label", aria);
      }
      grid.appendChild(cell);
    }

    monthEl.appendChild(grid);
    return monthEl;
  }

  /* -- Change / clear -- */
  var changeBtn = el("ovuChange");
  if (changeBtn) {
    changeBtn.addEventListener("click", function () {
      results.classList.remove("show");
      if (aside) aside.style.display = "";
      if (formGrid) formGrid.classList.remove("hh-ovu-card__grid--solo");
      formGrid.scrollIntoView({ behavior: prefersReduced() ? "auto" : "smooth", block: "start" });
      lastPeriodBtn.focus();
    });
  }

  function clearAll() {
    for (var ci = 0; ci < pickers.length; ci++) pickers[ci].reset();
    cycleSelect.value = String(OVU_CONFIG.defaultCycleLength);
    if (avgResult) avgResult.classList.remove("show");
    if (avgVary) avgVary.classList.remove("show");
    var clampNote = el("ovuAvgClamped");
    if (clampNote) { clampNote.textContent = ""; clampNote.classList.remove("show"); }
    results.classList.remove("show");
    errSummary.classList.remove("show");
    caution.classList.remove("show");
    if (earlyNote) earlyNote.classList.remove("show");
    var stronger = el("ovuStronger");
    if (stronger) stronger.classList.remove("show");
    el("ovuMonths").innerHTML = "";
    setInlineError(lastPeriodBtn, el("ovuLastPeriodErr"), "");
    setInlineError(cycleSelect, el("ovuCycleErr"), "");
    setInlineError(null, threeErr, "");
    radios.forEach(function (x, i) {
      x.classList.remove("selected");
      x.setAttribute("aria-checked", "false");
      x.tabIndex = i === 0 ? 0 : -1;
    });
    regularity = null;
    if (aside) aside.style.display = "";
    if (formGrid) formGrid.classList.remove("hh-ovu-card__grid--solo");
    if (helperPanel && helperPanel.classList.contains("open")) {
      helperPanel.classList.remove("open");
      helperToggle.setAttribute("aria-expanded", "false");
      helperToggle.textContent = helperToggle.getAttribute("data-open-label");
    }
  }

  var clearBtn = el("ovuClear");
  if (clearBtn) {
    clearBtn.addEventListener("click", function () {
      clearAll();
      formGrid.scrollIntoView({ behavior: prefersReduced() ? "auto" : "smooth", block: "start" });
      lastPeriodBtn.focus();
    });
  }

  /* -- Formula expander -- */
  var formulaToggle = el("ovuFormulaToggle");
  if (formulaToggle) {
    formulaToggle.addEventListener("click", function () {
      var opened = el("ovuFormula").classList.toggle("open");
      formulaToggle.setAttribute("aria-expanded", opened ? "true" : "false");
      formulaToggle.textContent = opened
        ? formulaToggle.getAttribute("data-close-label")
        : formulaToggle.getAttribute("data-open-label");
    });
  }

  /* -- Privacy -- */
  window.addEventListener("pagehide", clearAll);
  window.addEventListener("beforeunload", clearAll);
  window.addEventListener("pageshow", function (e) {
    if (e.persisted) clearAll();
  });
})();